import { MoneyWingsIcon } from '#app/__generated__/components/Icons/MoneyWings.js';
import { Text } from '#app/components/Primitives/Text.js';
import {
  SIDEBAR_CLOSED_WIDTH,
  SIDEBAR_OPEN_WIDTH,
} from '#app/constants/sidenav.js';
import { useAddUserInterfaceFlag } from '#app/hooks/useAddUserInterfaceFlag.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { useRouter } from '#app/hooks/useRouter.js';
import { ChevronRightIcon, CloseIcon } from '@contra/icons/icons';
import { getRoute } from '@contra/routes';
import { useCallback } from 'react';
import { css, styled } from 'styled-components';

const StyledBanner = styled.div<{ $isSidenavDocked: boolean }>`
  ${({ $isSidenavDocked, theme }) => css`
    background: ${theme.colorsV2.gray05};
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    min-height: 56px;
    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
    align-items: center;
    padding: 16px;
    background: linear-gradient(
      119deg,
      #222834 -17.98%,
      #6a57e3 36.89%,
      #cd74dd 82.18%
    );
    font-size: 16px;
    z-index: ${theme.zIndices.toolbar};
    line-height: 24px;
    font-weight: 400;
    color: ${theme.colorsV2.white};

    ${theme.mediaQueries.lg} {
      left: ${$isSidenavDocked ? SIDEBAR_OPEN_WIDTH : SIDEBAR_CLOSED_WIDTH}px;
      width: calc(
        100% - ${$isSidenavDocked ? SIDEBAR_OPEN_WIDTH : SIDEBAR_CLOSED_WIDTH}px
      );
    }
  `}
`;

const StyledText = styled(Text)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TextLink = styled.button`
  ${({ theme }) => css`
    color: ${theme.colorsV2.white};
    text-decoration: none;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 4px;
  `}
`;

const CloseButton = styled.button`
  ${({ theme }) => css`
    color: ${theme.colorsV2.white};
    position: absolute;
    right: 16px;
    top: 16px;
  `}
`;

const Spacer = styled.div`
  height: 56px;
`;

export const C4cReferralBanner = ({
  isSidenavDocked,
}: {
  readonly isSidenavDocked: boolean;
}) => {
  const isMobile = useIsMobile();
  const currentUser = useCurrentUser();
  const [addUserInterfaceFlag, isAddingUserInterfaceFlag] =
    useAddUserInterfaceFlag();
  const { track } = useAnalytics();
  const navigation = useNavigation();

  const { isActive } = useRouter();

  const handleClose = useCallback(() => {
    track('button', {
      action: 'tapped',
      button_name: 'c4c_referral_banner_close',
    });

    addUserInterfaceFlag({
      variables: {
        input: {
          userInterfaceFlag: 'HIDE_C4C_REFERRAL_BANNER',
        },
      },
    });
  }, [addUserInterfaceFlag, track]);

  const onClick = useCallback(() => {
    track('button', {
      action: 'tapped',
      button_name: 'c4c_referral_banner_view_details',
    });

    navigation.push(getRoute('/refer-and-earn'));
  }, [navigation, track]);

  if (currentUser?.selectedUserType !== 'CLIENT') {
    return null;
  }

  const uiFlags = currentUser.userInterfaceFlags;

  if (uiFlags.includes('HIDE_C4C_REFERRAL_BANNER')) {
    return null;
  }

  if (isActive(getRoute('/messages'), false)) {
    return null;
  }

  if (isAddingUserInterfaceFlag) {
    return null;
  }

  return (
    <>
      <StyledBanner $isSidenavDocked={isSidenavDocked}>
        <StyledText>
          <MoneyWingsIcon size={24} />
          {isMobile ? (
            <span>Refer and get $100</span>
          ) : (
            <span>Refer your network and get $100</span>
          )}
          <TextLink onClick={onClick}>
            View details
            <ChevronRightIcon size={24} />
          </TextLink>
        </StyledText>
        <CloseButton
          aria-label="Close banner"
          onClick={handleClose}
        >
          <CloseIcon />
        </CloseButton>
      </StyledBanner>

      {/* Element above is fixed but we still need to displace navbar */}
      <Spacer />
    </>
  );
};
